import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { PREORDERS_TABLE_TAB } from '@ekhodealer/ekho-common';

import type { PaginatedResponse,PreordersTableRow, PreordersTableTab } from '@ekhodealer/ekho-common';

import { PLACEHOLDER } from '@common/lib/strings';

import { useSearchPreorders } from '../api/useSearchPreorders';
import { preordersParamsAtom } from '../atoms/preordersAtoms';

/**
 * Hook to fetch and calculate preorder counts for each tab
 * Returns a function to get the count for a specific tab as a string
 * PLACEHOLDER is returned if query is pending, a number string for actual count, and '0' only if result is actually 0
 */
export function usePreordersTableTabCounts(): (tab: PreordersTableTab) => string {
	// Get params from atom (includes search, tab filter, and facets)
	const tableParams = useAtomValue(preordersParamsAtom);

	// Fetch preorder counts for all tabs using facets (no tab filter for tab counts)
	const { data: preordersData, isPending } = useSearchPreorders({
		filters: {
			...tableParams.filters,
			tabs: [], // No tab filter for tab counts
		},
		facets: ['status'],
		limit: 0, // We only need facets, not the data
	});

	// Calculate counts for each tab using raw Algolia status facets
	// Status facets come back keyed by PreorderExternalStatus enum (UPPERCASE)
	// Tab values are lowercase — map by lowercasing the status key
	const getTabCount = useMemo(() => {
		return (tab: PreordersTableTab): string => {
			if (isPending) {
				return PLACEHOLDER;
			}

			const response = preordersData as PaginatedResponse<PreordersTableRow> | undefined;
			const facets = response?.facets as Record<string, Record<string, number>> | undefined;
			const statusFacets = facets?.status ?? {};
			const totalCount = response?.count ?? 0;

			if (tab === PREORDERS_TABLE_TAB.ALL) {
				return totalCount.toLocaleString();
			}
			// Match tab (lowercase) to status enum (UPPERCASE)
			const count = statusFacets[tab.toUpperCase()] ?? 0;
			return count.toLocaleString();
		};
	}, [preordersData, isPending]);

	return getTabCount;
}
